/**
 * Dashboard – Система за мониторинг.
 * Актуална информация за устройствата, поддръжката и инспекциите.
 *
 * Логика: Секциите (преглед, устройства, поддръжка, инспекции, алерти, монитори) винаги се показват,
 * дори при липса на обекти или при грешка от API – тогава с празни стойности (0 устройства, „Няма данни“).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import './Dashboard.css';
import { apiService, getIsDemoMode } from '../services/apiWrapper';
import { getDemoDashboard } from '../services/demoData';
import { useLanguage } from '../i18n/LanguageContext';

const getStatusConfig = (t) => ({
  active: { label: t('dashboard.active'), icon: '✅', className: 'stat-online' },
  maintenance: { label: t('dashboard.maintenance'), icon: '🛠️', className: 'stat-maintenance' },
  faulty: { label: t('dashboard.faulty'), icon: '🚨', className: 'stat-alarm' },
  inactive: { label: t('dashboard.inactive'), icon: '⏸️', className: 'stat-offline' },
  offline: { label: t('dashboard.offline'), icon: '📴', className: 'stat-offline' }
});

const ACTIVITY_ICONS = {
  alert: '🚨',
  maintenance: '🛠️',
  inspection: '🔍'
};

const EMPTY_DASHBOARD = {
  deviceStats: {},
  criticalAlerts: 0,
  overdueMaintenance: 0,
  recentInspections: { total: 0, passed: 0, failed: 0 },
  devicesNeedingAttention: [],
  recentActivity: []
};

/** Връща винаги валиден обект за dashboard – секциите се показват дори при липса на устройства или невалидни данни */
function ensureDashboardShape(val) {
  if (val && typeof val === 'object' && !Array.isArray(val)) {
    return {
      deviceStats: val.deviceStats && typeof val.deviceStats === 'object' ? val.deviceStats : {},
      criticalAlerts: typeof val.criticalAlerts === 'number' ? val.criticalAlerts : 0,
      overdueMaintenance: typeof val.overdueMaintenance === 'number' ? val.overdueMaintenance : 0,
      recentInspections: val.recentInspections && typeof val.recentInspections === 'object' ? val.recentInspections : { total: 0, passed: 0, failed: 0 },
      devicesNeedingAttention: Array.isArray(val.devicesNeedingAttention) ? val.devicesNeedingAttention : [],
      recentActivity: Array.isArray(val.recentActivity) ? val.recentActivity : []
    };
  }
  return { ...EMPTY_DASHBOARD };
}

function Dashboard() {
  const { t } = useLanguage();
  const [dashboardData, setDashboardData] = useState(EMPTY_DASHBOARD);
  const [loading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [, setLoadingTime] = useState(0);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'devices', 'maintenance', 'inspections', 'alerts', 'monitors'
  const [monitorsData, setMonitorsData] = useState({ sprinklerZones: [], electricalPanels: [], evacDoors: [] });
  const [monitorsLoading, setMonitorsLoading] = useState(false);
  const isMountedRef = useRef(true);
  const hasLoadedOnceRef = useRef(false);

  // При монтиране изчистваме грешка, за да не се показва „Dashboard данните са невалидни. Очакван е обект.“ от предишен рендер
  useEffect(() => {
    setError(null);
  }, []);

  // При всяка промяна на error – ако е съобщение за невалидни данни, изчистваме го
  useEffect(() => {
    const msg = typeof error === 'string' ? error : (error?.message || '');
    if (msg && (msg.includes('Очакван е обект') || msg.includes('невалидни') || msg.includes('Expected') || msg.includes('Dashboard данните') || msg.includes('обект') || msg.includes('данните са'))) {
      setError(null);
      setDashboardData(EMPTY_DASHBOARD);
    }
  }, [error]);
  
  // Track loading time
  useEffect(() => {
    if (loading) {
      setLoadingTime(0);
      const interval = setInterval(() => {
        setLoadingTime(prev => prev + 1);
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setLoadingTime(0);
    }
  }, [loading]);

  const fetchMonitors = useCallback(async () => {
    setMonitorsLoading(true);
    try {
      const token = localStorage.getItem('fireguard_token');
      if (!token) {
        throw new Error('No authentication token');
      }

      const [zonesRes, panelsRes, doorsRes] = await Promise.all([
        apiService.sprinklerMonitor.getZones().catch(() => ({ data: { zones: [] } })),
        apiService.electricalPanelMonitor.getPanels().catch(() => ({ data: { panels: [] } })),
        apiService.evacDoorMonitor.getDoors().catch(() => ({ data: { doors: [] } }))
      ]);

      setMonitorsData({
        sprinklerZones: zonesRes.data?.zones || [],
        electricalPanels: panelsRes.data?.panels || [],
        evacDoors: doorsRes.data?.doors || []
      });
    } catch (error) {
      console.error('Грешка при зареждане на монитори:', error);
    } finally {
      setMonitorsLoading(false);
    }
  }, []);

  const fetchDashboard = useCallback(async ({ autoRefresh = false } = {}) => {
    setRefreshing(true);

    if (getIsDemoMode()) {
      try {
        const demo = getDemoDashboard();
        if (demo && demo.dashboard) {
          setDashboardData(ensureDashboardShape(demo.dashboard));
          setError(null);
          hasLoadedOnceRef.current = true;
          setLastUpdated(new Date());
          setRefreshing(false);
          return;
        }
      } catch (e) {
        console.warn('Demo dashboard load failed', e);
      }
    }
    if (getIsDemoMode()) {
      try {
        const d = getDemoDashboard();
        if (d && d.dashboard) setDashboardData(ensureDashboardShape(d.dashboard));
      } catch (_) {}
      setRefreshing(false);
      return;
    }

    try {
      const token = localStorage.getItem('fireguard_token');
      if (!token) {
        throw new Error('No authentication token. Please log in again.');
      }
      
      let response;
      try {
        response = await apiService.monitoring.getDashboard();
      } catch (requestError) {
        try {
          const apiUrl = process.env.NODE_ENV === 'production' ? 'https://app.firesguard.net' : (process.env.REACT_APP_API_URL || 'http://localhost:5000');
          const healthCheck = await fetch(`${apiUrl}/api/health`);
          await healthCheck.json();
          throw new Error('Backend is running but dashboard endpoint did not respond. Check backend logs.');
        } catch (healthError) {
          throw requestError;
        }
      }

      if (!isMountedRef.current) {
        return;
      }

      const raw = response?.data;
      const emptyShape = {
        deviceStats: {},
        criticalAlerts: 0,
        overdueMaintenance: 0,
        recentInspections: { total: 0, passed: 0, failed: 0 },
        devicesNeedingAttention: [],
        recentActivity: []
      };

      // Ако сървърът върне HTML – показваме празен dashboard и не хвърляме (секциите се виждат)
      if (typeof raw === 'string') {
        const looksLikeHtml = raw.trim().startsWith('<') || raw.includes('<!DOCTYPE') || raw.includes('<html');
        if (looksLikeHtml) {
          setDashboardData(emptyShape);
          setError('Server returned HTML instead of data. Check that the API is available (URL with /api).');
          setRefreshing(false);
          return;
        }
      }

      let dashboardData = null;
      if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
        if (raw.dashboard && typeof raw.dashboard === 'object' && !Array.isArray(raw.dashboard)) {
          dashboardData = raw.dashboard;
        } else if (raw.deviceStats !== undefined || raw.criticalAlerts !== undefined || raw.recentInspections !== undefined) {
          dashboardData = raw;
        } else {
          dashboardData = raw;
        }
      }

      // При липсващи или невалидни данни показваме празен dashboard (секциите се зареждат), без да хвърляме
      if (dashboardData === null || dashboardData === undefined || typeof dashboardData !== 'object' || Array.isArray(dashboardData)) {
        dashboardData = emptyShape;
      }

      const normalized = {
        deviceStats: dashboardData.deviceStats && typeof dashboardData.deviceStats === 'object' ? dashboardData.deviceStats : {},
        criticalAlerts: typeof dashboardData.criticalAlerts === 'number' ? dashboardData.criticalAlerts : 0,
        overdueMaintenance: typeof dashboardData.overdueMaintenance === 'number' ? dashboardData.overdueMaintenance : 0,
        recentInspections: dashboardData.recentInspections && typeof dashboardData.recentInspections === 'object' ? dashboardData.recentInspections : { total: 0, passed: 0, failed: 0 },
        devicesNeedingAttention: Array.isArray(dashboardData.devicesNeedingAttention) ? dashboardData.devicesNeedingAttention : [],
        recentActivity: Array.isArray(dashboardData.recentActivity) ? dashboardData.recentActivity : []
      };
      setDashboardData(normalized);
      hasLoadedOnceRef.current = true;
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      if (!isMountedRef.current) {
        return;
      }

      if (getIsDemoMode()) {
        try {
          const demo = getDemoDashboard();
          if (demo && demo.dashboard) {
            const d = demo.dashboard;
            setDashboardData(ensureDashboardShape(d));
            setError(null);
            hasLoadedOnceRef.current = true;
            setLastUpdated(new Date());
            setRefreshing(false);
            return;
          }
        } catch (demoErr) {
          console.warn('Demo fallback failed', demoErr);
        }
      }

      let errorMessage = 'Failed to load dashboard data.';
      
      if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
        const apiUrl = process.env.NODE_ENV === 'production' ? 'https://app.firesguard.net' : (process.env.REACT_APP_API_URL || 'http://localhost:5000');
        errorMessage = `Заявката отне твърде много време (timeout). Проверете дали backend сървърът работи на ${apiUrl}`;
      } else if (err.code === 'ERR_NETWORK' || err.message.includes('Network Error') || err.message.includes('Failed to fetch')) {
        // Вместо да показваме грешка и да използваме mock данни,
        // показваме празен dashboard (нула устройства, нула задачи и т.н.)
        console.warn('Dashboard: network грешка, показваме празни стойности вместо mock данни.', err);
        
        setDashboardData({
          deviceStats: {},
          criticalAlerts: 0,
          overdueMaintenance: 0,
          recentInspections: { total: 0, passed: 0, failed: 0 },
          devicesNeedingAttention: [],
          recentActivity: []
        });
        setError(null);
        return;
      } else if (err.response) {
        if (err.response.status === 401) {
          errorMessage = 'Invalid or expired authentication token. Please log in again.';
        } else if (err.response.status === 404) {
          errorMessage = 'Dashboard endpoint not found. Check backend configuration.';
        } else if (err.response.status >= 500) {
          errorMessage = `Backend сървърна грешка (${err.response.status}). Проверете backend логовете.`;
        } else {
          errorMessage = err.response.data?.message || `Грешка ${err.response.status}: ${err.response.statusText}`;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      // „Dashboard данните са невалидни“, „Очакван е обект“, „Expected“ – не показваме грешка, само празен dashboard (секциите се виждат)
      const isInvalidDataError = typeof errorMessage === 'string' && (
        errorMessage.includes('Очакван е обект') ||
        errorMessage.includes('Expected') ||
        errorMessage.includes('невалидни') ||
        errorMessage.includes('Dashboard данните')
      );
      if (isInvalidDataError) {
        setDashboardData({
          deviceStats: {},
          criticalAlerts: 0,
          overdueMaintenance: 0,
          recentInspections: { total: 0, passed: 0, failed: 0 },
          devicesNeedingAttention: [],
          recentActivity: []
        });
        setError(null);
        setRefreshing(false);
        return;
      }

      // Винаги показваме секциите на dashboard – при грешка с празни данни и съобщение отгоре
      setDashboardData({
        deviceStats: {},
        criticalAlerts: 0,
        overdueMaintenance: 0,
        recentInspections: { total: 0, passed: 0, failed: 0 },
        devicesNeedingAttention: [],
        recentActivity: []
      });
      setError(errorMessage);
    } finally {
      if (!isMountedRef.current) {
        return;
      }

      setRefreshing(false);
    }
  }, []); // Празен dependency array - fetchDashboard не зависи от нищо

  useEffect(() => {
    isMountedRef.current = true;
    fetchDashboard();
    fetchMonitors();

    const intervalId = setInterval(() => {
      if (isMountedRef.current) {
      fetchDashboard({ autoRefresh: true });
      }
    }, 60000);

    return () => {
      isMountedRef.current = false;
      clearInterval(intervalId);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount once, fetchDashboard stable
  }, []);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) {
      return '—';
    }
    try {
      return new Date(timestamp).toLocaleString('bg-BG', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return timestamp;
    }
  };

  const STATUS_CONFIG = getStatusConfig(t);

  const getStatusLabel = (statusKey) => {
    if (!statusKey) {
      return { label: '', icon: '📟', className: 'stat-generic' };
    }
    const normalized = String(statusKey).toLowerCase();
    if (STATUS_CONFIG[normalized]) {
      return STATUS_CONFIG[normalized];
    }
    return {
      label: String(statusKey),
      icon: '📟',
      className: 'stat-generic'
    };
  };

  // Винаги показваме shell (заглавие + табове + секции); при loading – спинър само в съдържанието, не празен екран
  // Данните винаги са нормализирани до валиден обект (дори при 0 устройства)
  const dataToShow = ensureDashboardShape(dashboardData);
  // Никога не показваме „Dashboard данните са невалидни. Очакван е обект.“ – филтрираме всички варианти
  const errorStr = error == null ? '' : (typeof error === 'string' ? error : (error?.message || String(error)));
  const hideAsInvalidData = errorStr && (
    errorStr.includes('Очакван е обект') || errorStr.includes('невалидни') || errorStr.includes('Expected') ||
    errorStr.includes('Dashboard данните') || errorStr.includes('обект') || errorStr.includes('данните са')
  );
  const displayError = errorStr && !hideAsInvalidData ? errorStr : null;

  // Безопасно деструктуриране на данните
  const deviceStats = dataToShow.deviceStats || {};
  const criticalAlerts = dataToShow.criticalAlerts ?? 0;
  const overdueMaintenance = dataToShow.overdueMaintenance ?? 0;
  const recentInspections = dataToShow.recentInspections || {};
  const devicesNeedingAttention = Array.isArray(dataToShow.devicesNeedingAttention) ? dataToShow.devicesNeedingAttention : [];
  const recentActivity = Array.isArray(dataToShow.recentActivity) ? dataToShow.recentActivity : [];
  
  const totalDevices = deviceStats && typeof deviceStats === 'object' 
    ? Object.values(deviceStats).reduce((sum, value) => sum + (Number(value) || 0), 0)
    : 0;

  const lastUpdatedLabel = lastUpdated
    ? lastUpdated.toLocaleTimeString('bg-BG', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : '—';

  return (
    <div className="dashboard-container">
      {displayError && (
        <div className="dashboard-error" style={{ marginBottom: '20px' }}>
          <p>⚠️ {displayError}</p>
          <button className="refresh-button" onClick={async (e) => {
            e?.preventDefault?.();
            e?.stopPropagation?.();
            try {
              await fetchDashboard();
            } catch (error) {
              console.error('Error in refresh button onClick:', error);
            }
          }}>
            🔄 Опитай отново
          </button>
        </div>
      )}
      <div className="dashboard-header">
        <div>
          <h1>📊 {t('dashboard.title')}</h1>
          <p className="dashboard-subtitle">{t('dashboard.subtitle')}</p>
        </div>
        <div className="dashboard-actions">
          <span className="refresh-status">
            {refreshing ? t('dashboard.refreshing') : `${t('dashboard.lastUpdated')}: ${lastUpdatedLabel}`}
          </span>
          <button
            className="refresh-button"
            onClick={async (e) => {
              e?.preventDefault?.();
              e?.stopPropagation?.();
              try {
                await fetchDashboard();
              } catch (error) {
                console.error('Error in refresh button onClick:', error);
              }
            }}
            disabled={refreshing}
          >
            ↻
          </button>
        </div>
      </div>

      {displayError && (
        <div className="dashboard-error" role="alert">
          <p>{displayError}</p>
        </div>
      )}

      {/* Табове – винаги видими, за да има секции от мониторинга дори при липса на обекти */}
      <div className="dashboard-tabs">
        <button
          className={`dashboard-tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          📊 {t('dashboard.devicesTab')}
        </button>
        <button
          className={`dashboard-tab ${activeTab === 'devices' ? 'active' : ''}`}
          onClick={() => setActiveTab('devices')}
        >
          📱 {t('devices.title')}
        </button>
        <button
          className={`dashboard-tab ${activeTab === 'maintenance' ? 'active' : ''}`}
          onClick={() => setActiveTab('maintenance')}
        >
          🛠️ {t('dashboard.maintenanceTab')}
        </button>
        <button
          className={`dashboard-tab ${activeTab === 'inspections' ? 'active' : ''}`}
          onClick={() => setActiveTab('inspections')}
        >
          🔍 {t('dashboard.inspectionsTab')}
        </button>
        <button
          className={`dashboard-tab ${activeTab === 'alerts' ? 'active' : ''}`}
          onClick={() => setActiveTab('alerts')}
        >
          🚨 {t('dashboard.alertsTab')}
        </button>
        <button
          className={`dashboard-tab ${activeTab === 'monitors' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('monitors');
            fetchMonitors();
          }}
        >
          🔥 {t('dashboard.monitorsTab')}
        </button>
      </div>

      {/* Контейнер за секциите – винаги видим, за да се показват преглед, устройства, поддръжка, инспекции, алерти, монитори */}
      <div className="dashboard-tab-content" style={{ minHeight: '400px', padding: '20px 0' }}>
      {loading ? (
        <div className="dashboard-section loading-section" style={{ minHeight: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px' }}>
          <div style={{ textAlign: 'center' }}>
            <div className="spinner" style={{ border: '4px solid rgba(255,255,255,0.3)', borderTop: '4px solid white', borderRadius: '50%', width: '48px', height: '48px', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
            <p>⏳ {t('dashboard.loading')}</p>
            <button type="button" className="refresh-button" style={{ marginTop: '12px' }} onClick={() => fetchDashboard()}>🔄 {t('dashboard.retry')}</button>
          </div>
        </div>
      ) : (
        <>
      {activeTab === 'overview' && (
        <>
          {/* Статистики - 2 реда по 3 картички */}
          {/* Първи ред */}
          <div className="stats-grid">
            <div className="stat-card stat-total">
              <div className="stat-icon-wrapper">
                <div className="stat-icon">📱</div>
              </div>
              <div className="stat-content">
                <div className="stat-value">{totalDevices}</div>
                <div className="stat-label">{t('dashboard.totalDevices')}</div>
                <div className="stat-description">{t('dashboard.allRegisteredDevices')}</div>
              </div>
            </div>

            <div className="stat-card stat-alerts">
              <div className="stat-icon-wrapper">
                <div className="stat-icon">🚨</div>
              </div>
              <div className="stat-content">
                <div className="stat-value">{criticalAlerts}</div>
                <div className="stat-label">{t('dashboard.criticalAlerts')}</div>
                <div className="stat-description">{t('dashboard.requiresImmediateAttention')}</div>
              </div>
            </div>

            <div className="stat-card stat-maintenance">
              <div className="stat-icon-wrapper">
                <div className="stat-icon">🛠️</div>
              </div>
              <div className="stat-content">
                <div className="stat-value">{overdueMaintenance}</div>
                <div className="stat-label">{t('dashboard.overdueTasks')}</div>
                <div className="stat-description">{t('dashboard.maintenanceAndRepair')}</div>
              </div>
            </div>
          </div>

          {/* Втори ред */}
          <div className="stats-grid">
            {(() => {
              // Ако има данни в deviceStats, показваме първия статус
              if (deviceStats && typeof deviceStats === 'object' && Object.entries(deviceStats).length > 0) {
                const firstStatus = Object.entries(deviceStats)[0];
                const { label, icon, className } = getStatusLabel(firstStatus[0]);
                return (
                  <div key={firstStatus[0]} className={`stat-card ${className}`}>
                    <div className="stat-icon-wrapper">
                      <div className="stat-icon">{icon}</div>
                    </div>
                    <div className="stat-content">
                      <div className="stat-value">{firstStatus[1] || 0}</div>
                      <div className="stat-label">{label}</div>
                      <div className="stat-description">{t('dashboard.devicesByStatus')} "{label}"</div>
                    </div>
                  </div>
                );
              }

              // Ако няма deviceStats, показваме празна картичка, за да запазим 3 на ред
              return (
                <div className="stat-card stat-generic">
                  <div className="stat-icon-wrapper">
                    <div className="stat-icon">📟</div>
                  </div>
                  <div className="stat-content">
                    <div className="stat-value">0</div>
                    <div className="stat-label">{t('dashboard.noStatusData')}</div>
                    <div className="stat-description">{t('dashboard.noStatusDataDesc')}</div>
                  </div>
                </div>
              );
            })()}

            <div className="stat-card stat-attention large-card">
              <div className="stat-icon-wrapper">
                <div className="stat-icon">⚠️</div>
              </div>
              <div className="stat-content">
                <div className="stat-value">{devicesNeedingAttention.length}</div>
                <div className="stat-label">{t('dashboard.devicesNeedingAttention')}</div>
                <div className="stat-description">{t('dashboard.requiresImmediateAttention')}</div>
                <div className="attention-list-compact">
                  {devicesNeedingAttention.length === 0 ? (
                    <div className="no-data-compact">{t('dashboard.noDevicesNeedAttention')}</div>
                  ) : (
                    devicesNeedingAttention.slice(0, 3).map((device) => (
                      <div key={device.id} className="attention-item-compact">
                        <span className="attention-name-compact">{device.name || `${t('dashboard.device')} #${device.id}`}</span>
                        <span className="attention-badges-compact">
                          {device.critical_alerts > 0 && <span className="badge-critical">🚨 {device.critical_alerts}</span>}
                          {device.overdue_tasks > 0 && <span className="badge-overdue">🕑 {device.overdue_tasks}</span>}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <div className="stat-card stat-inspection large-card">
              <div className="stat-icon-wrapper">
                <div className="stat-icon">🔍</div>
              </div>
              <div className="stat-content">
                <div className="stat-value">{recentInspections.total || 0}</div>
                <div className="stat-label">{t('dashboard.inspections')}</div>
                <div className="stat-description">{t('dashboard.last7Days')}</div>
                <div className="stat-meta">
                  <span className="stat-meta-item">✅ {recentInspections.passed || 0} {t('dashboard.passed')}</span>
                  <span className="stat-meta-item">❌ {recentInspections.failed || 0} {t('dashboard.failed')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Голяма картичка - Последна активност */}
          <div className="stat-card stat-activity full-width-card">
            <div className="stat-icon-wrapper">
              <div className="stat-icon">📋</div>
            </div>
            <div className="stat-content full-width-content">
              <div className="stat-label-large">{t('dashboard.recentActivity')}</div>
              <div className="activity-list-full">
                {recentActivity.length === 0 ? (
                  <div className="no-data">{t('dashboard.noActivity')}</div>
                ) : (
                  recentActivity.map((activity, index) => (
                    <div key={`${activity.type}-${activity.timestamp}-${index}`} className={`activity-item activity-${activity.type}`}>
                      <div className="activity-icon">{ACTIVITY_ICONS[activity.type] || 'ℹ️'}</div>
                      <div className="activity-content">
                        <div className="activity-title">{activity.description}</div>
                        <div className="activity-meta">
                          <span>{formatTimestamp(activity.timestamp)}</span>
                          {activity.device_id && <span>{t('dashboard.device')} #{activity.device_id}</span>}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {activeTab === 'devices' && (
        <div className="dashboard-section">
          <div className="section-header">
            <h2>📱 {t('dashboard.deviceInfo')}</h2>
          </div>
          <div className="info-grid">
            <div className="info-card">
              <h3>{t('dashboard.totalDevicesLabel')}</h3>
              <div className="info-value">{totalDevices}</div>
              <div className="info-description">{t('dashboard.allRegisteredDevices')}</div>
            </div>
            {deviceStats && typeof deviceStats === 'object' && Object.entries(deviceStats).map(([statusKey, count]) => {
              const { label, icon } = getStatusLabel(statusKey);
              return (
                <div key={statusKey} className="info-card">
                  <h3>{icon} {label}</h3>
                  <div className="info-value">{count || 0}</div>
                  <div className="info-description">{t('dashboard.devicesByStatus')} "{label}"</div>
                </div>
              );
            })}
          </div>
          <div className="dashboard-section" style={{ marginTop: '30px' }}>
            <h2>⚠️ {t('dashboard.devicesNeedingAttention')}</h2>
            {devicesNeedingAttention.length === 0 ? (
              <div className="no-data">{t('dashboard.noDevicesNeedAttention')}</div>
            ) : (
              devicesNeedingAttention.map((device) => (
                <div key={device.id} className="attention-item">
                  <div className="attention-title">
                    <span className="attention-name">{device.name || `${t('dashboard.device')} #${device.id}`}</span>
                    <span className={`attention-status attention-status-${(device.status || 'unknown').toLowerCase()}`}>
                      {device.status || t('common.error')}
                    </span>
                  </div>
                  <div className="attention-meta">
                    <span>{device.type || 'Unknown type'}</span>
                    <span>{device.location || 'Unknown location'}</span>
                  </div>
                  <div className="attention-stats">
                    <span>🚨 Critical alerts: {device.critical_alerts || 0}</span>
                    <span>🕑 Overdue tasks: {device.overdue_tasks || 0}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {activeTab === 'maintenance' && (
        <div className="dashboard-section">
          <h2>🛠️ {t('dashboard.maintenanceTab')}</h2>
          <div className="info-grid">
            <div className="info-card">
              <h3>{t('dashboard.overdueTasks')}</h3>
              <div className="info-value">{overdueMaintenance}</div>
              <div className="info-description">{t('dashboard.overdueMaintenance')}</div>
            </div>
          </div>
          <div className="dashboard-section" style={{ marginTop: '30px' }}>
            <h2>📋 {t('dashboard.recentActivity')}</h2>
            {recentActivity.filter(a => a.type === 'maintenance').length === 0 ? (
              <div className="no-data">{t('dashboard.noActivity')}</div>
            ) : (
              recentActivity.filter(a => a.type === 'maintenance').map((activity, index) => (
                <div key={`${activity.type}-${activity.timestamp}-${index}`} className={`activity-item activity-${activity.type}`}>
                  <div className="activity-icon">{ACTIVITY_ICONS[activity.type] || 'ℹ️'}</div>
                  <div className="activity-content">
                    <div className="activity-title">{activity.description}</div>
                    <div className="activity-meta">
                      <span>{formatTimestamp(activity.timestamp)}</span>
                      {activity.device_id && <span>{t('dashboard.device')} #{activity.device_id}</span>}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {activeTab === 'inspections' && (
        <div className="dashboard-section">
          <h2>🔍 Inspection information</h2>
          <div className="info-grid">
            <div className="info-card">
              <h3>Total inspections</h3>
              <div className="info-value">{recentInspections.total || 0}</div>
              <div className="info-description">Last 7 days</div>
            </div>
            <div className="info-card">
              <h3>Passed</h3>
              <div className="info-value">{recentInspections.passed || 0}</div>
              <div className="info-description">Successful inspections</div>
            </div>
            <div className="info-card">
              <h3>Failed</h3>
              <div className="info-value">{recentInspections.failed || 0}</div>
              <div className="info-description">Inspections with issues</div>
            </div>
          </div>
          <div className="dashboard-section" style={{ marginTop: '30px' }}>
            <h2>📋 Inspection activity (24h)</h2>
            {recentActivity.filter(a => a.type === 'inspection').length === 0 ? (
              <div className="no-data">No inspection activity in the last 24 hours.</div>
            ) : (
              recentActivity.filter(a => a.type === 'inspection').map((activity, index) => (
                <div key={`${activity.type}-${activity.timestamp}-${index}`} className={`activity-item activity-${activity.type}`}>
                  <div className="activity-icon">{ACTIVITY_ICONS[activity.type] || 'ℹ️'}</div>
                  <div className="activity-content">
                    <div className="activity-title">{activity.description}</div>
                    <div className="activity-meta">
                      <span>{formatTimestamp(activity.timestamp)}</span>
                      {activity.device_id && <span>Device #{activity.device_id}</span>}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {activeTab === 'alerts' && (
        <div className="dashboard-section">
          <h2>🚨 Alert information</h2>
          <div className="info-grid">
            <div className="info-card">
              <h3>Critical alerts</h3>
              <div className="info-value">{criticalAlerts}</div>
              <div className="info-description">Require immediate attention</div>
            </div>
          </div>
          <div className="dashboard-section" style={{ marginTop: '30px' }}>
            <h2>📋 Alert activity (24h)</h2>
            {recentActivity.filter(a => a.type === 'alert').length === 0 ? (
              <div className="no-data">No alert activity in the last 24 hours.</div>
            ) : (
              recentActivity.filter(a => a.type === 'alert').map((activity, index) => (
                <div key={`${activity.type}-${activity.timestamp}-${index}`} className={`activity-item activity-${activity.type}`}>
                  <div className="activity-icon">{ACTIVITY_ICONS[activity.type] || 'ℹ️'}</div>
                  <div className="activity-content">
                    <div className="activity-title">{activity.description}</div>
                    <div className="activity-meta">
                      <span>{formatTimestamp(activity.timestamp)}</span>
                      {activity.device_id && <span>Device #{activity.device_id}</span>}
                    </div>
                  </div>
                </div>
              ))
            )}
        </div>
      </div>
      )}

      {activeTab === 'monitors' && (
        <div className="dashboard-section">
          <h2>🔥 Fire safety monitors</h2>
          
          {monitorsLoading ? (
            <div className="loading-container" style={{ padding: '40px', textAlign: 'center' }}>
              <div className="spinner" style={{ 
                border: '4px solid rgba(255, 255, 255, 0.3)', 
                borderTop: '4px solid white', 
                borderRadius: '50%', 
                width: '50px', 
                height: '50px', 
                animation: 'spin 1s linear infinite',
                margin: '0 auto 20px'
              }}></div>
              <p>Loading monitors...</p>
            </div>
          ) : (
            <>
              {/* Sprinkler zones */}
              <div className="monitor-section" style={{ marginBottom: '30px' }}>
                <h3>💧 Sprinkler zones ({monitorsData.sprinklerZones.length})</h3>
                {monitorsData.sprinklerZones.length === 0 ? (
                  <div className="no-data">No sprinkler zones registered</div>
                ) : (
                  <div className="monitor-grid">
                    {monitorsData.sprinklerZones.map((zone) => (
                      <div key={zone.id} className={`monitor-card monitor-${zone.state?.toLowerCase() || 'normal'}`}>
                        <div className="monitor-header">
                          <span className="monitor-icon">💧</span>
                          <span className="monitor-name">{zone.name}</span>
                          <span className={`monitor-state monitor-state-${zone.state?.toLowerCase() || 'normal'}`}>
                            {zone.state || 'NORMAL'}
                          </span>
                        </div>
                        <div className="monitor-details">
                          <div className="monitor-location">📍 {zone.location}</div>
                          {zone.pressure_main !== null && (
                            <div className="monitor-metric">
                              Pressure: <strong>{zone.pressure_main?.toFixed(1)} bar</strong>
                            </div>
                          )}
                          {zone.last_alarm_at && (
                            <div className="monitor-timestamp">
                              Last ALARM: {new Date(zone.last_alarm_at).toLocaleString('en-GB')}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Electrical panels */}
              <div className="monitor-section" style={{ marginBottom: '30px' }}>
                <h3>⚡ Electrical panels ({monitorsData.electricalPanels.length})</h3>
                {monitorsData.electricalPanels.length === 0 ? (
                  <div className="no-data">No electrical panels registered</div>
                ) : (
                  <div className="monitor-grid">
                    {monitorsData.electricalPanels.map((panel) => (
                      <div key={panel.id} className={`monitor-card monitor-${panel.state?.toLowerCase() || 'normal'}`}>
                        <div className="monitor-header">
                          <span className="monitor-icon">⚡</span>
                          <span className="monitor-name">{panel.name}</span>
                          <span className={`monitor-state monitor-state-${panel.state?.toLowerCase() || 'normal'}`}>
                            {panel.state || 'NORMAL'}
                          </span>
                        </div>
                        <div className="monitor-details">
                          <div className="monitor-location">📍 {panel.location}</div>
                          {panel.temp_panel !== null && (
                            <div className="monitor-metric">
                              Temperature: <strong>{panel.temp_panel?.toFixed(1)}°C</strong>
                            </div>
                          )}
                          {panel.last_alarm_at && (
                            <div className="monitor-timestamp">
                              Last ALARM: {new Date(panel.last_alarm_at).toLocaleString('en-GB')}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Evacuation doors */}
              <div className="monitor-section">
                <h3>🚪 Evacuation doors ({monitorsData.evacDoors.length})</h3>
                {monitorsData.evacDoors.length === 0 ? (
                  <div className="no-data">No evacuation doors registered</div>
                ) : (
                  <div className="monitor-grid">
                    {monitorsData.evacDoors.map((door) => (
                      <div key={door.id} className={`monitor-card monitor-${door.state?.toLowerCase() || 'normal'}`}>
                        <div className="monitor-header">
                          <span className="monitor-icon">🚪</span>
                          <span className="monitor-name">{door.name}</span>
                          <span className={`monitor-state monitor-state-${door.state?.toLowerCase() || 'normal'}`}>
                            {door.state || 'NORMAL'}
                          </span>
                        </div>
                        <div className="monitor-details">
                          <div className="monitor-location">📍 {door.location}</div>
                          <div className="monitor-metric">
                            Type: <strong>{door.door_type === 'evacuation' ? 'Evacuation' : 'Fire-resistant'}</strong>
                          </div>
                          <div className="monitor-status">
{door.door_closed ? '🔒 Closed' : '🔓 Open'} |
                            {door.panic_bar_ok ? ' ✅ Panic bar OK' : ' ⚠️ Panic bar issue'} |
                            {door.lock_engaged ? ' 🔐 Locked' : ' 🔓 Unlocked'}
                          </div>
                          {door.last_alarm_at && (
                            <div className="monitor-timestamp">
                              Last ALARM: {new Date(door.last_alarm_at).toLocaleString('en-GB')}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
        </>
      )}
      </div>
    </div>
  );
}

export default Dashboard;
